import { useState, useCallback, useEffect, useRef, Fragment } from 'react';
import { Search, X, ArrowUp, ArrowDown, Loader2, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from './input';
import { Checkbox } from './checkbox';
import { Pagination } from './pagination';
import { Skeleton } from './skeleton';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from './table';
import type { SortState } from '@/types';

export interface DataTableColumn<T> {
  key: string;
  label: string;
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
  render: (item: T) => React.ReactNode;
}

export interface DataTableMobileRenderContext {
  rowId: string;
  isSelected: boolean;
  selectedCount: number;
  selectable: boolean;
  toggleSelected: () => void;
  /** True when the table supports expandable rows. */
  expandable: boolean;
  /** Whether this row's expanded panel is currently open. */
  isExpanded: boolean;
  /** Toggle this row's expanded panel. */
  toggleExpanded: () => void;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  total: number;
  page: number;
  perPage: number;
  sort: SortState;
  search: string;
  isLoading: boolean;
  isFetching?: boolean;
  selectable?: boolean;
  selectedIds?: Set<string>;
  /** Enables a leading expand/collapse chevron column. */
  expandable?: boolean;
  /** Renders the panel shown beneath a row when it is expanded. */
  renderExpanded?: (item: T) => React.ReactNode;
  getRowId: (item: T) => string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: SortState) => void;
  onSearchChange: (search: string) => void;
  onSelectionChange?: (ids: Set<string>) => void;
  onRowClick?: (item: T) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  emptySubMessage?: string;
  filterBar?: React.ReactNode;
  mobileRender?: (item: T, context: DataTableMobileRenderContext) => React.ReactNode;
}

function isInteractiveTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    target.closest('button, a, input, select, textarea, [role="checkbox"], [data-no-row-click]')
  );
}

function DataTable<T>({
  columns,
  data,
  total,
  page,
  perPage,
  sort,
  search,
  isLoading,
  isFetching,
  selectable = false,
  selectedIds,
  expandable = false,
  renderExpanded,
  getRowId,
  onPageChange,
  onSortChange,
  onSearchChange,
  onSelectionChange,
  onRowClick,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  emptySubMessage,
  filterBar,
  mobileRender,
}: DataTableProps<T>) {
  const [searchInput, setSearchInput] = useState(search);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isSearchPending =
    Boolean(searchInput) && (searchInput !== search || Boolean(isFetching));

  // Sync external search changes
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const handleSearchInput = useCallback(
    (value: string) => {
      setSearchInput(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearchChange(value);
        onPageChange(1);
      }, 300);
    },
    [onSearchChange, onPageChange],
  );

  const clearSearch = useCallback(() => {
    setSearchInput('');
    onSearchChange('');
    onPageChange(1);
  }, [onSearchChange, onPageChange]);

  const handleSort = useCallback(
    (key: string) => {
      if (sort.field === key) {
        onSortChange({
          field: key,
          direction: sort.direction === 'asc' ? 'desc' : 'asc',
        });
      } else {
        onSortChange({ field: key, direction: 'asc' });
      }
    },
    [sort, onSortChange],
  );

  // Selection
  const allPageIds = data.map(getRowId);
  const allSelected = selectable && allPageIds.length > 0 && allPageIds.every((id) => selectedIds?.has(id));
  const someSelected = selectable && allPageIds.some((id) => selectedIds?.has(id)) && !allSelected;

  const toggleAll = useCallback(() => {
    if (!onSelectionChange || !selectedIds) return;
    const next = new Set(selectedIds);
    if (allSelected) {
      allPageIds.forEach((id) => next.delete(id));
    } else {
      allPageIds.forEach((id) => next.add(id));
    }
    onSelectionChange(next);
  }, [allSelected, allPageIds, selectedIds, onSelectionChange]);

  const toggleOne = useCallback(
    (id: string) => {
      if (!onSelectionChange || !selectedIds) return;
      const next = new Set(selectedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange],
  );

  const selectedCount = selectedIds?.size ?? 0;

  // Row expansion
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  /** Total column count including the leading checkbox / chevron cells. */
  const totalColumnCount =
    columns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0);

  const sortableColumns = columns.filter((col) => col.sortable);
  const hasMobileCards = mobileRender != null;

  const handleMobileSortField = useCallback(
    (field: string) => {
      if (!sortableColumns.some((col) => col.key === field)) return;
      onSortChange({ field, direction: sort.direction });
      onPageChange(1);
    },
    [sortableColumns, sort.direction, onSortChange, onPageChange],
  );

  const toggleMobileSortDirection = useCallback(() => {
    onSortChange({
      field: sort.field,
      direction: sort.direction === 'asc' ? 'desc' : 'asc',
    });
    onPageChange(1);
  }, [sort, onSortChange, onPageChange]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Search + Filters */}
      <div className="flex shrink-0 flex-col gap-2 border-b border-border px-3 py-2.5 sm:px-4 md:flex-row md:flex-wrap md:items-center md:gap-3">
        <div className="relative w-full min-w-0 md:max-w-sm md:flex-1 md:min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-10 pl-8 pr-14 text-base sm:text-sm md:h-8 md:text-[13px]"
          />
          {searchInput && (
            <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-1">
              {isSearchPending && (
                <Loader2
                  role="status"
                  aria-label="Search in progress"
                  className="h-3.5 w-3.5 animate-spin text-muted-foreground"
                />
              )}
              <button
                type="button"
                onClick={clearSearch}
                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
        {filterBar && (
          <div className="flex w-full min-w-0 flex-col gap-2 md:w-auto md:flex-1 md:flex-row md:flex-wrap md:items-center md:gap-3">
            {filterBar}
          </div>
        )}
        {sortableColumns.length > 0 && (
          <div className="flex w-full items-center gap-2 md:hidden">
            <select
              value={sort.field}
              onChange={(e) => handleMobileSortField(e.target.value)}
              aria-label="Sort results"
              className="h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-base text-foreground outline-none focus:ring-1 focus:ring-ring"
            >
              {sortableColumns.map((col) => (
                <option key={col.key} value={col.key}>
                  Sort by {col.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={toggleMobileSortDirection}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent"
              aria-label={
                sort.direction === 'asc'
                  ? 'Switch to descending sort'
                  : 'Switch to ascending sort'
              }
            >
              {sort.direction === 'asc' ? (
                <ArrowUp className="h-3.5 w-3.5" />
              ) : (
                <ArrowDown className="h-3.5 w-3.5" />
              )}
              {sort.direction === 'asc' ? 'Asc' : 'Desc'}
            </button>
          </div>
        )}
        {isFetching && !isLoading && !searchInput && (
          <Loader2 className="h-4 w-4 animate-spin self-end text-muted-foreground md:ml-auto md:self-auto" />
        )}
      </div>

      {/* Table — single bounded scroll container so the header stays sticky */}
      <div className="flex flex-1 flex-col min-h-0">
        {isLoading ? (
          <>
            {hasMobileCards && (
              <div className="flex flex-1 flex-col gap-2 overflow-auto p-3 md:hidden">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="rounded-lg border border-border bg-card p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-12 w-12 rounded-md" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Table wrapperClassName={cn('flex-1 min-h-0', hasMobileCards && 'hidden md:block')}>
              <TableHeader>
                <TableRow>
                  {selectable && (
                    <TableHead className="w-[56px]">
                      <Skeleton className="h-4 w-4" />
                    </TableHead>
                  )}
                  {expandable && <TableHead className="w-[44px]" />}
                  {columns.map((col) => (
                    <TableHead key={col.key} className={col.headerClassName}>
                      <Skeleton className="h-3 w-16" />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 12 }).map((_, i) => (
                  <TableRow key={i}>
                    {selectable && (
                      <TableCell>
                        <Skeleton className="h-4 w-4" />
                      </TableCell>
                    )}
                    {expandable && <TableCell />}
                    {columns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <p className="text-sm">{emptyMessage}</p>
            {emptySubMessage && <p className="text-xs mt-1">{emptySubMessage}</p>}
          </div>
        ) : (
          <>
            {hasMobileCards && (
              <div className="flex flex-1 flex-col gap-2 overflow-auto p-3 md:hidden">
                {data.map((item) => {
                  const rowId = getRowId(item);
                  const isSelected = selectedIds?.has(rowId) ?? false;
                  const isExpanded = expandedIds.has(rowId);
                  return (
                    <div
                      key={rowId}
                      data-state={isSelected ? 'selected' : undefined}
                      role={onRowClick ? 'button' : undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      className={cn(
                        'rounded-lg border border-border bg-card p-3 shadow-sm outline-none transition-colors',
                        onRowClick && 'cursor-pointer active:bg-muted/70',
                        isSelected && 'border-primary/50 bg-primary/5',
                      )}
                      onClick={(e) => {
                        if (isInteractiveTarget(e.target)) return;
                        onRowClick?.(item);
                      }}
                      onKeyDown={(e) => {
                        if (!onRowClick || isInteractiveTarget(e.target)) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(item);
                        }
                      }}
                    >
                      {mobileRender?.(item, {
                        rowId,
                        isSelected,
                        selectedCount,
                        selectable,
                        toggleSelected: () => toggleOne(rowId),
                        expandable,
                        isExpanded,
                        toggleExpanded: () => toggleExpand(rowId),
                      })}
                      {expandable && isExpanded && renderExpanded && (
                        <div
                          data-no-row-click
                          className="-mx-3 -mb-3 mt-3 rounded-b-lg border-t border-border bg-muted/30"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {renderExpanded(item)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <Table wrapperClassName={cn('flex-1 min-h-0', hasMobileCards && 'hidden md:block')}>
              <TableHeader>
                <TableRow className="bg-card hover:bg-card">
                  {selectable && (
                    <TableHead
                      className="w-[56px] cursor-pointer"
                      onClick={(e) => {
                        if (isInteractiveTarget(e.target)) return;
                        toggleAll();
                      }}
                    >
                      <Checkbox
                        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                        onCheckedChange={toggleAll}
                      />
                    </TableHead>
                  )}
                  {expandable && <TableHead className="w-[44px]" />}
                  {columns.map((col) => (
                    <TableHead key={col.key} className={col.headerClassName}>
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col.key)}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          {col.label}
                          {sort.field === col.key && (
                            sort.direction === 'asc' ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : (
                              <ArrowDown className="h-3 w-3" />
                            )
                          )}
                        </button>
                      ) : (
                        col.label
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((item) => {
                  const rowId = getRowId(item);
                  const isSelected = selectedIds?.has(rowId) ?? false;
                  const isExpanded = expandedIds.has(rowId);
                  return (
                    <Fragment key={rowId}>
                      <TableRow
                        data-state={isSelected ? 'selected' : undefined}
                        className={cn(onRowClick && 'cursor-pointer')}
                        onClick={(e) => {
                          if (isInteractiveTarget(e.target)) return;
                          onRowClick?.(item);
                        }}
                      >
                        {selectable && (
                          <TableCell
                            className="w-[56px] cursor-pointer"
                            onClick={(e) => {
                              // The whole cell toggles the row — clicking anywhere
                              // in this column selects, and never navigates the row.
                              e.stopPropagation();
                              if (isInteractiveTarget(e.target)) return;
                              toggleOne(rowId);
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleOne(rowId)}
                            />
                          </TableCell>
                        )}
                        {expandable && (
                          <TableCell className="w-[44px]">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(rowId);
                              }}
                              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                              aria-expanded={isExpanded}
                            >
                              <ChevronRight
                                className={cn(
                                  'h-4 w-4 transition-transform',
                                  isExpanded && 'rotate-90',
                                )}
                              />
                            </button>
                          </TableCell>
                        )}
                        {columns.map((col) => (
                          <TableCell key={col.key} className={col.className}>
                            {col.render(item)}
                          </TableCell>
                        ))}
                      </TableRow>
                      {expandable && isExpanded && renderExpanded && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={totalColumnCount} className="bg-muted/30 p-0">
                            {renderExpanded(item)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </div>

      {/* Footer / Pagination */}
      <div className="flex shrink-0 flex-col gap-2 border-t border-border px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <p className="text-center text-xs text-muted-foreground sm:text-left">
          {total > 0
            ? `${(page - 1) * perPage + 1}-${Math.min(page * perPage, total)} of ${total}`
            : '0 items'}
        </p>
        {total > perPage && (
          <Pagination
            total={total}
            page={page}
            perPage={perPage}
            onChange={onPageChange}
            className="sm:justify-end"
          />
        )}
      </div>
    </div>
  );
}

export { DataTable };
